import { TourType } from '../interfaces/tour-type.enum';

/**
 * Copy for the showing-agent field and the surfaces that describe who that agent is.
 *
 * On an in-person or virtual tour the agent hosts the showing, so "held by" is accurate. On a
 * self-guided tour nobody attends — the renter lets themselves in — and the agent is only an
 * escalation contact. Owners read "Showing will be held by" as "someone must be there", so the
 * self-guided arm reframes the same field as a backup contact.
 *
 * Four components across two Nx projects render these strings; they live here so they can't drift.
 */
export interface ShowingAgentCopy {
  /** Label above the ShowingAgentPicker. */
  fieldLabel: string;
  /** Clarifier under the picker. Undefined renders nothing, which is today's markup. */
  fieldHint?: string;
  /** Validation message when no agent is picked. Must read correctly under `fieldLabel`. */
  fieldRequiredError: string;
  /** Heading of the agent card on the showing details page. */
  cardTitle: string;
  /** Body of that card, already interpolated with the agent's first name. */
  cardDescription: string;
  /** Body of that card when the showing has no agent yet. */
  emptyCardDescription: string;
  /** Stands where the agent's name would be when the showing has none. */
  emptyAgentLabel: string;
  /** Header of the modal opened from that card. */
  editModalTitle: string;
  /** Header of that modal when it sets the first agent rather than replacing one. */
  addModalTitle: string;
}

export interface AcceptShowingStep {
  title: string;
  description: string;
}

export interface AcceptShowingCopy {
  /** Already interpolated with the renter's first name. */
  title: string;
  subtitle: string;
  /** Rendered in order; the step numbers come from the loop index, not from here. */
  steps: AcceptShowingStep[];
}

type ShowingAgentCopyTemplate = Omit<ShowingAgentCopy, 'cardDescription'> & {
  cardDescription: (agentFirstName: string) => string;
};

type AcceptShowingCopyTemplate = Omit<AcceptShowingCopy, 'title'> & {
  title: (renterFirstName: string) => string;
};

const DEFAULT_AGENT_COPY: ShowingAgentCopyTemplate = {
  fieldLabel: 'Showing will be held by',
  fieldRequiredError: 'Showing agent is required',
  cardTitle: 'Held by',
  cardDescription: (agentFirstName) =>
    `${agentFirstName} will be the one conducting the showing. Tallo will use ${agentFirstName}'s contact details to facilitate the showing`,
  emptyCardDescription:
    'No one is assigned to conduct this showing yet. Add who will hold it, and Tallo will use their contact details to facilitate the showing',
  emptyAgentLabel: 'No agent assigned',
  editModalTitle: 'Edit agent',
  addModalTitle: 'Add agent',
};

const SELF_GUIDED_AGENT_COPY: ShowingAgentCopyTemplate = {
  fieldLabel: 'Backup contact',
  fieldHint:
    "Who Tallo contacts if something comes up — a jammed lockbox, or a question Tallo can't answer. They don't need to be there.",
  fieldRequiredError: 'Pick a backup contact',
  cardTitle: 'Backup contact',
  cardDescription: (agentFirstName) =>
    `No one needs to attend a self-guided tour. If something comes up — a jammed lockbox, or a question Tallo can't answer — Tallo will reach out to ${agentFirstName}.`,
  emptyCardDescription:
    "No one needs to attend a self-guided tour, but there is no backup contact yet. Add one so Tallo has someone to reach if something comes up — a jammed lockbox, or a question Tallo can't answer.",
  emptyAgentLabel: 'No backup contact yet',
  editModalTitle: 'Edit backup contact',
  addModalTitle: 'Add backup contact',
};

const DEFAULT_ACCEPT_COPY: AcceptShowingCopyTemplate = {
  title: (renterFirstName) => `Accept ${renterFirstName}'s showing request!`,
  subtitle: 'Once accepted, showing details still can be updated from the showing page',
  steps: [
    {
      title: 'Tallo will confirm the showing',
      description: 'Tallo will let renter know the showing has been accepted',
    },
    {
      title: 'Send reminders',
      description: 'Tallo will send reminders two hours before the showing to both parties',
    },
    {
      title: 'Answer questions',
      description: 'If the renter has any questions along the way, Tallo will handle them',
    },
  ],
};

const SELF_GUIDED_ACCEPT_COPY: AcceptShowingCopyTemplate = {
  title: (renterFirstName) => `Accept ${renterFirstName}'s self-guided tour!`,
  subtitle: 'Once accepted, tour details still can be updated from the showing page',
  steps: [
    {
      title: 'Tallo will confirm the tour',
      description: 'Tallo will let the renter know the tour is confirmed',
    },
    {
      title: 'Send access instructions',
      description: 'Tallo sends the entry details to the renter before the tour. No one needs to be at the property.',
    },
    {
      title: 'Send reminders',
      description: 'Tallo will send reminders two hours before the tour to both parties',
    },
    {
      title: 'Answer questions',
      description: 'If the renter has any questions along the way, Tallo will handle them',
    },
  ],
};

/*
 * Full records rather than Partial + fallback: adding a fourth TourType should be a type error here,
 * not a silent inheritance of the in-person framing.
 */
const AGENT_COPY_BY_TOUR_TYPE: Record<TourType, ShowingAgentCopyTemplate> = {
  [TourType.IN_PERSON]: DEFAULT_AGENT_COPY,
  [TourType.VIRTUAL]: DEFAULT_AGENT_COPY,
  [TourType.SELF_GUIDED]: SELF_GUIDED_AGENT_COPY,
};

const ACCEPT_COPY_BY_TOUR_TYPE: Record<TourType, AcceptShowingCopyTemplate> = {
  [TourType.IN_PERSON]: DEFAULT_ACCEPT_COPY,
  [TourType.VIRTUAL]: DEFAULT_ACCEPT_COPY,
  [TourType.SELF_GUIDED]: SELF_GUIDED_ACCEPT_COPY,
};

/**
 * `tourType` is optional so a caller that can't supply it falls back to today's copy rather than
 * regressing — two of the consuming components receive it as an optional prop.
 */
export function getShowingAgentCopy(tourType?: TourType, agentFirstName = ''): ShowingAgentCopy {
  const template = (tourType && AGENT_COPY_BY_TOUR_TYPE[tourType]) || DEFAULT_AGENT_COPY;

  return { ...template, cardDescription: template.cardDescription(agentFirstName) };
}

export function getAcceptShowingCopy(tourType: TourType | undefined, renterFirstName: string): AcceptShowingCopy {
  const template = (tourType && ACCEPT_COPY_BY_TOUR_TYPE[tourType]) || DEFAULT_ACCEPT_COPY;

  return { ...template, title: template.title(renterFirstName) };
}
